import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { RouteTransition } from "./components/RouteTransition";

// Landing page loaded eagerly for fast initial paint
import Landing from "./pages/Landing";

// Retry wrapper for dynamic imports (handles stale cache after deploys)
function retryImport<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch(() => {
    window.location.reload();
    return importFn();
  });
}

// Lazy load all other pages for code splitting
const AdminDashboard = lazy(() => retryImport(() => import("./pages/AdminDashboard")));
const AdminLogin = lazy(() => retryImport(() => import("./pages/AdminLogin")));
const Demo = lazy(() => retryImport(() => import("./pages/Demo")));
const DemoPreview = lazy(() => retryImport(() => import("./pages/DemoPreview")));
const NotFound = lazy(() => retryImport(() => import("./pages/NotFound")));
const AdminVerification = lazy(() => retryImport(() => import("./pages/AdminVerification")));
const EstablishmentDashboard = lazy(() => retryImport(() => import("./pages/EstablishmentDashboard")));
const EstablishmentProfile = lazy(() => retryImport(() => import("./pages/EstablishmentProfile")));
const EstablishmentStatistics = lazy(() => retryImport(() => import("./pages/EstablishmentStatistics")));
const EstablishmentSettings = lazy(() => retryImport(() => import("./pages/EstablishmentSettings")));
const CreateFrame = lazy(() => retryImport(() => import('./pages/CreateFrame')));
const CreateCollageType = lazy(() => retryImport(() => import('./pages/CreateCollageType')));
const ManageCollageType = lazy(() => retryImport(() => import('./pages/ManageCollageType')));
const FrameDetails = lazy(() => retryImport(() => import('./pages/FrameDetails')));
const PaystackWebhookDocs = lazy(() => retryImport(() => import('./pages/PaystackWebhookDocs').then(m => ({ default: m.PaystackWebhookDocs }))));
const DeliverySettings = lazy(() => retryImport(() => import('./pages/DeliverySettings').then(m => ({ default: m.DeliverySettings }))));
const DeliveryZones = lazy(() => retryImport(() => import('./pages/DeliveryZones').then(m => ({ default: m.DeliveryZones }))));
const Guides = lazy(() => retryImport(() => import('./pages/Guides')));
const GuideDetail = lazy(() => retryImport(() => import('./pages/GuideDetail')));
const FAQ = lazy(() => retryImport(() => import('./pages/FAQ')));
const TermsOfService = lazy(() => retryImport(() => import('./pages/TermsOfService')));
const PrivacyPolicy = lazy(() => retryImport(() => import('./pages/PrivacyPolicy')));
const EstablishmentFrames = lazy(() => retryImport(() => import('./pages/EstablishmentFrames')));
const EstablishmentCreateQR = lazy(() => retryImport(() => import('./pages/EstablishmentCreateQR')));
const EstablishmentQRGallery = lazy(() => retryImport(() => import('./pages/EstablishmentQRGallery')));
const EstablishmentEditQR = lazy(() => retryImport(() => import('./pages/EstablishmentEditQR')));
const PublicEstablishmentCreate = lazy(() => retryImport(() => import('./pages/PublicEstablishmentCreate')));
const QRView = lazy(() => retryImport(() => import("./pages/QRView")));
const EstablishmentAuth = lazy(() => retryImport(() => import("./pages/EstablishmentAuth")));
const AudioPortraitDemo = lazy(() => retryImport(() => import("./pages/AudioPortraitDemo")));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <main>
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          {/* Public pages */}
          <Route path="/" element={<RouteTransition><Landing /></RouteTransition>} />
          <Route path="/demo" element={<RouteTransition><Demo /></RouteTransition>} />
          <Route path="/demo-preview" element={<RouteTransition><DemoPreview /></RouteTransition>} />
          <Route path="/demo/audio-portrait" element={<RouteTransition><AudioPortraitDemo /></RouteTransition>} />
          <Route path="/qr/:id" element={<RouteTransition><QRView /></RouteTransition>} />
          
          {/* Public creation flow (main flow) */}
          <Route path="/e/:slug" element={<RouteTransition><PublicEstablishmentCreate /></RouteTransition>} />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<RouteTransition><AdminLogin /></RouteTransition>} />
          <Route path="/admin" element={<RouteTransition><AdminDashboard /></RouteTransition>} />
          <Route path="/admin/verification" element={<RouteTransition><AdminVerification /></RouteTransition>} />
          
          {/* Establishment auth */}
          <Route path="/establishment/login" element={<RouteTransition><EstablishmentAuth /></RouteTransition>} />
          
          {/* Establishment routes (Artevo only) */}
          <Route path="/establishment/dashboard" element={<RouteTransition><EstablishmentDashboard /></RouteTransition>} />
          <Route path="/establishment/create-qr" element={<RouteTransition><EstablishmentCreateQR /></RouteTransition>} />
          <Route path="/establishment/qr-gallery" element={<RouteTransition><EstablishmentQRGallery /></RouteTransition>} />
          <Route path="/establishment/edit-qr/:id" element={<RouteTransition><EstablishmentEditQR /></RouteTransition>} />
          <Route path="/establishment/frames" element={<RouteTransition><EstablishmentFrames /></RouteTransition>} />
          <Route path="/establishment/frames/create" element={<RouteTransition><CreateFrame /></RouteTransition>} />
          <Route path="/establishment/collages/create" element={<RouteTransition><CreateCollageType /></RouteTransition>} />
          <Route path="/establishment/collages/:id" element={<RouteTransition><ManageCollageType /></RouteTransition>} />
          <Route path="/establishment/frames/:id" element={<RouteTransition><FrameDetails /></RouteTransition>} />
          <Route path="/establishment/profile" element={<RouteTransition><EstablishmentProfile /></RouteTransition>} />
          <Route path="/establishment/statistics" element={<RouteTransition><EstablishmentStatistics /></RouteTransition>} />
          <Route path="/establishment/delivery-settings" element={<RouteTransition><DeliverySettings /></RouteTransition>} />
          <Route path="/establishment/delivery-zones" element={<RouteTransition><DeliveryZones /></RouteTransition>} />
          <Route path="/establishment/settings" element={<RouteTransition><EstablishmentSettings /></RouteTransition>} />
          
          {/* Informative pages */}
          <Route path="/guides" element={<RouteTransition><Guides /></RouteTransition>} />
          <Route path="/guides/:guideId" element={<RouteTransition><GuideDetail /></RouteTransition>} />
          <Route path="/faq" element={<RouteTransition><FAQ /></RouteTransition>} />
          <Route path="/terms" element={<RouteTransition><TermsOfService /></RouteTransition>} />
          <Route path="/privacy" element={<RouteTransition><PrivacyPolicy /></RouteTransition>} />
          <Route path="/paystack/webhook-docs" element={<RouteTransition><PaystackWebhookDocs /></RouteTransition>} />
          
          {/* Catch-all */}
          <Route path="*" element={<RouteTransition><NotFound /></RouteTransition>} />
        </Routes>
      </Suspense>
    </main>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
